// Links INTO the Explorer, from the areas that deep-link at it.
//
// Five areas point here — Alerts, Big clients, Funnel, Searches and Talent
// Management — and every area is granted independently. So an on-call reviewer
// holding `alerts` alone (the whole point of that split: triage flags without
// reading every conversation) used to see a project link on every row, click it,
// and land on the access-required panel. The link was drawn from the row's data;
// nothing asked whether its DESTINATION would open.
//
// These two components ask. A viewer who cannot open the Explorer gets the same
// text without the anchor, so the row still says which project or talent it is
// about: hiding the LINK is not hiding the FACT, and the fact is what makes the
// worklist readable. Where the anchor is the only content (a bare arrow in an
// actions column), pass `denied={null}` and it is dropped entirely.
//
// This is manners, not the boundary — `<AreaGate>` on the destination route and
// the area gate on its API are what actually refuse. But a console whose links
// are honest is the difference between "I do not have that" and "this is broken".
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";

import { useCanOpenExplorer } from "../lib/explorer-access";

type DeepLinkProps = {
  className?: string;
  /** Only where the link's own text does not say where it goes (an icon). */
  ariaLabel?: string;
  children: ReactNode;
  /** What to draw instead when the Explorer is not this viewer's to open.
   *  Defaults to the children as plain text. Pass `null` to render nothing,
   *  which is right for an icon-only affordance whose row already names the
   *  record elsewhere. */
  denied?: ReactNode;
};

/** Has a `denied` been supplied? `undefined` means "not given" and falls back to
 *  plain text; an explicit `null` means "draw nothing" and must survive. */
function fallback(denied: ReactNode | undefined, children: ReactNode): ReactNode {
  return denied === undefined ? <span>{children}</span> : denied;
}

export function ExplorerProjectLink({
  projectId,
  className,
  ariaLabel,
  children,
  denied,
}: { projectId: string } & DeepLinkProps) {
  const canOpen = useCanOpenExplorer();
  if (!canOpen) return <>{fallback(denied, children)}</>;
  return (
    <Link
      to="/explorer/projects/$projectId"
      params={{ projectId }}
      className={className}
      aria-label={ariaLabel}
    >
      {children}
    </Link>
  );
}

/** `projectId` scopes the talent page to ONE engagement (the Searches tab wants
 *  "this hunt", the dashboard wants "this talent overall"), so it stays optional
 *  and is passed straight through as the destination's `?project=`. */
export function ExplorerTalentLink({
  sellerId,
  projectId,
  className,
  ariaLabel,
  children,
  denied,
}: { sellerId: string; projectId?: string } & DeepLinkProps) {
  const canOpen = useCanOpenExplorer();
  if (!canOpen) return <>{fallback(denied, children)}</>;
  return (
    <Link
      to="/explorer/talents/$sellerId"
      params={{ sellerId }}
      search={projectId ? { project: projectId } : {}}
      className={className}
      aria-label={ariaLabel}
    >
      {children}
    </Link>
  );
}
